import { useNetworkState } from '@uidotdev/usehooks';
import { useSimpleMatrixClient } from 'hooks/matrix/useSimpleMatrixClient';
import { useCallback, useEffect, useState } from 'react';
import { errorEventEmitter } from '../utils/errorEmitter';

export const useHealthCheck = () => {
  const network = useNetworkState();
  const matrix = useSimpleMatrixClient();
  const isMatrixAvailable = Boolean(matrix?.isLoggedIn()) && Boolean(matrix?.isInitialSyncComplete());

  const [backendOnline, setBackendOnline] = useState(true);
  const [modal, setModal] = useState(false);

  const handleSuccess = useCallback(() => {
    setBackendOnline(true);
    setModal(false);

    if (matrix && !matrix.clientRunning && network.online) {
      matrix.startClient();
    }
  }, [matrix, network.online]);

  const handleFailure = useCallback(() => {
    setBackendOnline(false);
    setModal(true);

    if (matrix?.clientRunning) {
      matrix.stopClient();
    }
  }, [matrix]);

  const fetchHealth = useCallback(async () => {
    // Skip health check if browser is offline
    if (!network.online) {
      handleFailure();
      return;
    }

    try {
      const resp = await fetch(import.meta.env.VITE_APP_BACKEND_URI + '/_health', {
        headers: {
          'Cache-Control': 'no-cache, no-store',
          Pragma: 'no-cache',
          'If-None-Match': '', // Prevent 304 responses
          'If-Modified-Since': '',
        },
      });

      if (!resp.ok) {
        throw new Error('Health check failed');
      }

      handleSuccess();
    } catch (_error: unknown) {
      console.error(_error);
      handleFailure();
    }
  }, [network.online, handleSuccess, handleFailure]);

  // listen to GraphQL errors
  useEffect(() => {
    // Subscribe to error events
    const unsubscribe = errorEventEmitter.subscribe((error) => {
      let message = 'An unexpected error occurred';

      if (error.graphQLErrors?.length) {
        message = error.graphQLErrors.map((e) => e.message).join('\n');
      } else if (error.networkError) {
        message = `Network error: ${error.networkError.message}`;
      }

      console.error(message);

      fetchHealth();
    });

    // Clean up subscription
    return unsubscribe;
  }, [fetchHealth]);

  // listen to Matrix Errors
  useEffect(() => {
    if (isMatrixAvailable) return;
    fetchHealth();
  }, [isMatrixAvailable, fetchHealth]);

  useEffect(() => {
    if (!modal) return;

    const interval = setInterval(fetchHealth, 5000);

    return () => {
      clearInterval(interval);
    };
  }, [modal, fetchHealth]);

  // Listen to browser online/offline
  useEffect(() => {
    const handleOnline = () => {
      void fetchHealth();
    };

    window.addEventListener('online', handleOnline);

    return () => {
      window.removeEventListener('online', handleOnline);
    };
  }, [fetchHealth]);

  return {
    modal,
    setModal,
    networkIsOnline: network.online,
    backendIsOnline: backendOnline,
    fetchHealth,
  };
};
